import { action, computed } from '@ember/object';
import { bannerError, callbackError } from 'client-portal/utils/error-handling';
import { reads } from '@ember/object/computed';
import { roundToCurrency } from 'client-portal/helpers/format-money';
import { success } from 'client-portal/utils/banners';
import { task } from 'ember-concurrency';
import { testAwareTimeout } from 'ember-simplepractice/utils/waiters';
import ENV from 'client-portal/config/environment';
import Service, { service } from '@ember/service';
import classic from 'ember-classic-decorator';
import generateUUID from 'ember-simplepractice/utils/generate-uuid';

@classic
export default class BillingModalsService extends Service {
  @service store;

  @service session;
  @service currentPractice;
  @service kountDeviceDataCollector;
  @service stripe;
  @service analytics;
  @service clientPortalTracking;
  @service intl;

  @reads('session.currentClient') client;

  @reads('client.visibleBalanceDue') balanceDue;
  @reads('client.visibleUnpaidInvoices') unpaidInvoices;
  @reads('currentPractice.hasCustomStripeAccount') hasCustomStripeAccount;
  @reads('persistPaymentWithPaymentMethodTask.isRunning') isPersisting;
  @reads('paymentModel.validations.isInvalid') isPaymentInvalid;
  @reads('paymentModel.amountType') paymentAmountType;

  pdfModel = null;
  paymentModel = null;
  afterPersist = [];

  init() {
    super.init(...arguments);

    this.hidePdf = this.hidePdf.bind(this);
    this.showPayment = this.showPayment.bind(this);
    this.hidePayment = this.hidePayment.bind(this);
    this.createNewCard = this.createNewCard.bind(this);
    this.setDefaultCard = this.setDefaultCard.bind(this);
    this.setPaymentInvoice = this.setPaymentInvoice.bind(this);
    this.setPaymentDetails = this.setPaymentDetails.bind(this);

    this.kountDeviceDataCollector.collectData();
  }

  @action
  showPdf(model) {
    this.set('pdfModel', model);
    model.set('opened', true);

    // Track when a receipt is opened
    if (model.constructor.modelName === 'receipt') {
      this.analytics.track('Client Portal Receipt Opened', {
        object: 'Receipt',
        action: 'Opened',
        'document_title': model.displayName,
        'object_id': model.id,
        'client_portal_client_access_id': this.session.currentClientAccess.id,
      });
    }
  }

  hidePdf() {
    this.set('pdfModel', null);
  }

  showPayment(invoice = null) {
    let payment = this.store.createRecord('payment');
    let card = this._defaultCard();

    payment.setProperties({
      card,
      _invoice: invoice,
    });

    this.set('paymentModel', payment);
    this.setPaymentInvoice(invoice);
  }

  hidePayment() {
    if (this.paymentModel.card && this.paymentModel.card.isNew) {
      this.paymentModel.card.deleteRecord();
    }
    this.paymentModel.deleteRecord();
    this.set('paymentModel', null);
  }

  createNewCard() {
    this.paymentModel.setProperties({ card: this._newCard() });
  }

  setDefaultCard() {
    this.paymentModel.card.deleteRecord();
    this.paymentModel.setProperties({ card: this._defaultCard() });
  }

  setPaymentInvoice(invoice = null) {
    let amount = invoice ? invoice.remainingAmount : this.balanceDue;
    let amountType = invoice ? 'invoice' : 'total';
    this.paymentModel.setProperties({ invoice, amount, maximumAmount: amount, amountType });
  }

  setPaymentDetails(paymentDetails) {
    this.paymentModel.setProperties(paymentDetails);
  }

  async confirmExpressCheckout(elements, paymentOptions) {
    let { error: submitError } = await elements.submit();
    if (submitError) return;

    this._trackClientPaymentAttempt('Attempted');
    let paymentIntent = await this.createPaymentIntent(paymentOptions);
    let { error: confirmError } = await this.stripe.stripe.confirmPayment({
      elements,
      clientSecret: paymentIntent.clientSecret,
      redirect: 'if_required',
    });

    if (confirmError) return;

    this.refreshBillingProfile.perform(paymentIntent);

    return paymentIntent;
  }

  @computed('unpaidInvoices.@each.remainingAmount')
  get totalUnpaidAmount() {
    let totalUnpaid = this.unpaidInvoices.reduce((acc, invoice) => {
      return (acc += invoice.remainingAmount || 0);
    }, 0);

    return roundToCurrency(totalUnpaid);
  }

  @computed('unpaidInvoices.@each.remainingAmount', 'paymentModel.invoice', 'paymentModel.amount')
  get invoiceIdsWithAmounts() {
    if (this.paymentModel.invoice) {
      return this._buildInvoiceIdsWithAmounts(
        [this.paymentModel.invoice],
        this.paymentModel.amount
      );
    } else {
      let paymentAmount = this.paymentModel.amount;

      // We only apply the account credit if the user is paying the full balance due.
      // simplepractice only allocates credit if the actual payment amount was less than what we said should be
      // allocated to that invoice. So in order to apply the credit, we need to allocate the full amount of
      // totalUnpaidAmount instead of only the payment amount.
      if (this.paymentModel.amount === this.balanceDue) {
        paymentAmount = this.totalUnpaidAmount;
      }

      return this._buildInvoiceIdsWithAmounts(this.unpaidInvoices.slice().reverse(), paymentAmount);
    }
  }

  _buildInvoiceIdsWithAmounts(invoices, paymentAmount) {
    return invoices.reduce((acc, invoice) => {
      let { id: invoiceId, remainingAmount } = invoice;

      if (remainingAmount > 0 && paymentAmount > 0) {
        let amount = Math.min(paymentAmount, remainingAmount);

        // Convert to cents to avoid floating point precision issues
        let paymentAmountCents = Math.round(paymentAmount * 100);
        let amountCents = Math.round(amount * 100);

        let calculatedAmountCents = paymentAmountCents - amountCents;

        paymentAmount = Math.max(0, calculatedAmountCents / 100);
        acc.push({ invoiceId, amount });
      }

      return acc;
    }, []);
  }

  @(task(function* (elements) {
    this.paymentModel.set('_isDirty', true);
    this._trackClientPaymentAttempt('Attempted');

    let { card: newCard } = this.paymentModel;
    let embed = [];

    try {
      if (newCard.isNew) {
        let { paymentMethod } = yield this.stripe.stripe.createPaymentMethod({
          elements,
          params: {
            /* eslint-disable camelcase */
            billing_details: {
              name: newCard.name,
            },
          },
        });
        let { card } = paymentMethod;

        newCard.setProperties({
          brand: card.brand,
          last4: card.last4,
          expMonth: card.exp_month,
          expYear: card.exp_year,
          paymentMethodId: paymentMethod.id,
        });

        if (newCard._saveCard) {
          embed = ['card'];
        } else {
          this.paymentModel.set('customCardToken', paymentMethod.id);
        }
      }

      yield this._createPayment(embed);
      yield Promise.all(this.afterPersist.map(x => x()));

      /*
        Resets the Stripe instance because of payment method caching by createPaymentMethod
        This is a temporary fix for edge cases with this flow, which is only needed to help migrate to Payment Methods
        This instance resetting should be removed once the payment element handles payment intents
      */
      this.stripe.setup(this.currentPractice.get('customStripePublishableKey'));

      this.hidePayment();
      success({ title: this.intl.t('billing.success.paymentMadeSuccessfully') });
    } catch (err) {
      this._handlePaymentFailure(err);
    }
  }).keepLatest())
  persistPaymentWithPaymentMethodTask;

  async createPaymentIntent(paymentOptions) {
    try {
      return await this._persistPaymentIntent(paymentOptions);
    } catch (error) {
      this._handlePaymentFailure(error);
    }
  }

  @task(function* (paymentIntent) {
    let calls = 0;
    let maxCalls = 6;

    let invoiceId = paymentIntent.invoiceIdsWithAmounts[0].invoiceId;

    while (calls < maxCalls) {
      let invoice = yield this.store.findRecord('invoice', invoiceId, { reload: true });

      if (invoice.get('isPaid') || ENV.environment === 'test') {
        yield Promise.all(this.afterPersist.map(x => x()));

        return;
      } else {
        yield testAwareTimeout(1000);
        calls++;
      }
    }

    yield Promise.all(this.afterPersist.map(x => x()));
  })
  refreshBillingProfile;

  async _persistPaymentIntent(paymentOptions) {
    let { invoiceIdsWithAmounts } = this;
    let { amount, currency } = paymentOptions;
    let paymentIntent = {
      amount,
      currency,
      paymentMethodTypes: ['card', 'link'],
      idempotencyKey: generateUUID(),
      useCredit: this._useCredit(),
      invoiceIdsWithAmounts,
    };
    return await this.store.createRecord('stripe-payment-intent', paymentIntent).save();
  }

  _defaultCard() {
    return this.client.cards.find(({ isDefault }) => isDefault) || this.client.cards.at(0);
  }

  _newCard() {
    let card = this.store.createRecord('card', {
      name: this.session.currentClientAccess.client.preferredName,
    });
    card.setProperties({
      _saveCard: true,
      isDefault: true,
    });
    return card;
  }

  _trackClientPaymentAttempt(action) {
    this.analytics.track('Client Portal Payment Attempted', {
      object: 'Client Portal Payment',
      action,
      'payment_amount_type': this.paymentAmountType,
      'payment_modal_type': this.paymentModel._invoice ? 'invoice' : 'total',
    });
  }

  _handlePaymentFailure(err) {
    this._trackClientPaymentAttempt('Failed');
    callbackError(err, message => {
      let title = this.intl.t('billing.success.paymentCouldNotBeMade');

      if (message.title.match(/Your account cannot currently make charges/)) {
        title = this.intl.t('billing.success.paymentCouldNotBeProcessedTitle');
        message.title = this.intl.t('billing.success.contactProviderForInfo');
      }

      this.clientPortalTracking.trackErrorOnBillingPage(title);
      bannerError(err, { title });
      return true;
    });
  }

  async _createPayment(embed) {
    let { invoiceIdsWithAmounts } = this;

    await this.store.adapterFor('payment').createWithInvoiceItemPayments({
      ...this.paymentModel.serialize({
        adapterOptions: {
          embed,
        },
      }),
      meta: {
        useCredit: this._useCredit(),
        invoiceIdsWithAmounts,
      },
    });
  }

  _useCredit() {
    return (
      !this.paymentModel.invoice &&
      this.balanceDue < this.totalUnpaidAmount &&
      this.paymentModel.amount === this.balanceDue
    );
  }
}
